<template>
    <span
        ref="trigger"
        class="cp-tooltip-wrap inline-flex relative align-middle"
        @mouseenter="updateViewportPosition"
        @focusin="updateViewportPosition"
    >
        <slot></slot>
        <span
            v-if="hasBubble"
            ref="bubble"
            class="cp-tooltip-bubble pointer-events-none absolute left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs leading-snug text-gray-700"
            :class="[widthClass, bubblePlacementClass, {'cp-tooltip-bubble--viewport': viewport}]"
            :style="[bubbleStyle, viewportBubbleStyle]"
        >
            <slot v-if="hasSlotContent" name="content"></slot>
            <template v-else>{{ text }}</template>
        </span>
    </span>
</template>

<script>
export default {
    name: 'app-tooltip',
    props: {
        text: {
            type: String,
            default: '',
        },
        widthClass: {
            type: String,
            default: 'w-72',
        },
        minWidth: {
            type: String,
            default: '',
        },
        maxWidth: {
            type: String,
            default: '',
        },
        fitContent: {
            type: Boolean,
            default: false,
        },
        bubbleZIndex: {
            type: Number,
            default: 260,
        },
        placement: {
            type: String,
            default: 'top',
        },
        viewport: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            viewportBubbleStyle: {},
        };
    },
    computed: {
        bubblePlacementClass() {
            return this.placement === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2';
        },
        bubbleStyle() {
            const style = {
                zIndex: String(this.bubbleZIndex),
            };

            if (this.fitContent) {
                style.width = 'max-content';
            }

            if (this.minWidth) {
                style.minWidth = this.minWidth;
            }

            if (this.maxWidth) {
                style.maxWidth = this.maxWidth;
            }

            return style;
        },
        hasSlotContent() {
            return !!this.$slots.content;
        },
        hasBubble() {
            return this.hasSlotContent || !!this.text;
        },
    },
    methods: {
        updateViewportPosition() {
            if (!this.viewport || !this.hasBubble) return;

            this.$nextTick(() => {
                const trigger = this.$refs.trigger;
                const bubble = this.$refs.bubble;
                if (!trigger || !bubble) return;

                const triggerRect = trigger.getBoundingClientRect();
                const bubbleRect = bubble.getBoundingClientRect();
                const viewportGap = 8;
                const bubbleWidth = bubbleRect.width || 288;
                const bubbleHeight = bubbleRect.height || 32;
                let placement = this.placement;

                if (placement === 'bottom' && triggerRect.bottom + viewportGap + bubbleHeight > window.innerHeight) {
                    placement = 'top';
                } else if (placement !== 'bottom' && triggerRect.top - viewportGap - bubbleHeight < 0) {
                    placement = 'bottom';
                }

                const left = Math.min(
                    Math.max(viewportGap, triggerRect.left + (triggerRect.width / 2) - (bubbleWidth / 2)),
                    Math.max(viewportGap, window.innerWidth - bubbleWidth - viewportGap)
                );
                const top = placement === 'bottom'
                    ? triggerRect.bottom + viewportGap
                    : triggerRect.top - bubbleHeight - viewportGap;

                this.viewportBubbleStyle = {
                    left: `${left}px`,
                    top: `${Math.max(viewportGap, top)}px`,
                };
            });
        },
    },
}
</script>

<style scoped>
.cp-tooltip-wrap {
    isolation: isolate;
    z-index: 1;
}

.cp-tooltip-bubble {
    background: #ffffff;
    border: 1px solid var(--ui-border-default);
    border-radius: 4px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.12s ease-in-out;
    white-space: normal;
}

.cp-tooltip-bubble--viewport {
    position: fixed !important;
    right: auto !important;
    bottom: auto !important;
    margin: 0 !important;
    transform: none !important;
}

.cp-tooltip-wrap:hover,
.cp-tooltip-wrap:focus-within {
    z-index: 250;
}

.cp-tooltip-wrap:hover .cp-tooltip-bubble,
.cp-tooltip-wrap:focus-within .cp-tooltip-bubble {
    opacity: 1;
    visibility: visible;
}
</style>
